import { useEffect, useSyncExternalStore } from 'react';
import { useRecoilValue } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    ContourAutoSuggestionController,
    type PendingContourSuggestion,
} from '~/engine/tool/contourAutoSuggestion/ContourAutoSuggestionController';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    CONTOUR_STROKE_TOOLS,
    selectedToolState,
    contourAutoSuggestionEnabledState,
} from '~/state/atoms/toolsAtoms';

/**
 * Drives the {@link ContourAutoSuggestionController} singleton from React
 * state. The controller is enabled iff ALL of the following hold:
 *   - the user toggled the feature on,
 *   - the active tool is one of {@link CONTOUR_STROKE_TOOLS},
 *   - the edit target resolves to an Environment: the registry
 *     Environment whose row shows the active structure set,
 *   - the {@link ViewManager} exists,
 *   - a non-point ROI is selected.
 *
 * The controller itself suggests, and commits, only while a view of the
 * Environment displays an image slice, so no gate on the views' geometry
 * is needed here.
 *
 * The controller is handed the Environment, the live set of its views and
 * the signal that one of them changed which image of the fused pair it
 * draws, so a tile that mounts, unmounts or changes sides reaches it
 * through those handles, not through a re-render here. The hook re-runs
 * `disable` then `enable` whenever one of the dependencies above changes so
 * the controller always observes the correct tool + Environment + structure
 * triple.
 */
export function useContourAutoSuggestionController(): void {
    const enabled = useRecoilValue(contourAutoSuggestionEnabledState);
    const tool = useRecoilValue(selectedToolState);
    const viewManager = useRecoilValue(viewManagerState);
    const { environment, roiId } = useActiveEditTarget();

    useEffect(() => {
        const controller = ContourAutoSuggestionController.getInstance();

        if (
            !enabled ||
            !CONTOUR_STROKE_TOOLS.includes(tool) ||
            !environment ||
            !viewManager ||
            roiId === null
        ) {
            controller.disable();
            return;
        }

        const roi = environment.getRoi(roiId);
        if (!roi || roi.isPointRoi) {
            controller.disable();
            return;
        }

        controller.enable(
            environment,
            viewManager.viewsFor(environment),
            (listener) =>
                viewManager.subscribeFusedImageDisplay(environment, listener),
            () => environment.getRoi(roiId),
            () => viewManager.interactionContext.toolManager.getActiveTool()
        );
        return () => {
            controller.disable();
        };
    }, [enabled, tool, environment, viewManager, roiId]);
}

function subscribeToPendingSuggestion(onChange: () => void): () => void {
    const { pendingSuggestionChanged } =
        ContourAutoSuggestionController.getInstance();
    pendingSuggestionChanged.subscribe(onChange);
    return () => pendingSuggestionChanged.unsubscribe(onChange);
}

/** The contour suggestion on offer, or undefined while there is none. */
export function usePendingContourSuggestion():
    | PendingContourSuggestion
    | undefined {
    return useSyncExternalStore(
        subscribeToPendingSuggestion,
        () => ContourAutoSuggestionController.getInstance().pendingSuggestion
    );
}
